import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import React, { useCallback, useMemo, useState } from 'react';
import { FlatList, Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { DateStrip } from '@/components/matches/DateStrip';
import { LiveBadge } from '@/components/ui/LiveBadge';
import { Screen } from '@/components/ui/Screen';
import { useThemePalette } from '@/config/theme';
import { useScores365, useSportsEvents } from '@/features/sports/hooks';
import { dayKey, formatKickoffTime, isSameDay, localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import {
  SCORES_365_SPORTS,
  type Scores365Sport,
  type SportId,
  type SportsEvent,
  type SportsMode,
} from '@/lib/data/provider';

/**
 * Every chip on the selector. A chip is sourced from EITHER Bzzoiro (the original
 * five, via `sports-events`) OR 365scores (the five team sports Bzzoiro lacks, via
 * `scores-365`). `i18nId` keys the display name in the locale files. The two
 * providers normalize to the same row shape, so the toggle + card render both.
 */
type Chip =
  | { source: 'bzzoiro'; sport: SportId; i18nId: string; emoji: string }
  | { source: '365'; sport: Scores365Sport; i18nId: string; emoji: string };

const SPORTS: Chip[] = [
  // Bzzoiro — tennis leads, it carries the most data (285 + serve stats, no rate limit).
  { source: 'bzzoiro', sport: 'tennis', i18nId: 'tennis', emoji: '🎾' },
  { source: 'bzzoiro', sport: 'csgo', i18nId: 'csgo', emoji: '🎮' },
  { source: 'bzzoiro', sport: 'darts', i18nId: 'darts', emoji: '🎯' },
  { source: 'bzzoiro', sport: 'hockey', i18nId: 'hockey', emoji: '🏒' },
  // 365scores — the new variety. Basketball is sourced from 365 (≈19 games/day vs
  // Bzzoiro's ≈1).
  { source: '365', sport: 'basketball', i18nId: 'basketball', emoji: '🏀' },
  { source: '365', sport: 'americanfootball', i18nId: 'americanFootball', emoji: '🏈' },
  { source: '365', sport: 'baseball', i18nId: 'baseball', emoji: '⚾' },
  { source: '365', sport: 'volleyball', i18nId: 'volleyball', emoji: '🏐' },
  { source: '365', sport: 'handball', i18nId: 'handball', emoji: '🤾' },
  { source: '365', sport: 'rugby', i18nId: 'rugby', emoji: '🏉' },
];

/** A chip's stable key (Bzzoiro sport id and 365 chip ids never collide). */
const chipKey = (c: Chip): string => c.sport;

const MODES: SportsMode[] = ['live', 'upcoming', 'finished'];

/**
 * An ISO-3166 alpha-2 country code → flag emoji (regional indicator pair).
 * Returns '' for anything that isn't a clean two-letter code, so a missing /
 * malformed code never renders mojibake next to the side's name.
 */
function flagFromCountryCode(code: string | null | undefined): string {
  if (!code || !/^[a-zA-Z]{2}$/.test(code)) return '';
  const upper = code.toUpperCase();
  const base = 0x1f1e6; // regional indicator 'A'
  return String.fromCodePoint(base + (upper.charCodeAt(0) - 65), base + (upper.charCodeAt(1) - 65));
}

/**
 * The first letter of a competitor's name, for the monogram avatar (no crests
 * exist in this data, so a clean lettermark is the honest, premium stand-in).
 * Falls back to '•' for an empty / symbol-only name.
 */
function monogram(name: string | null | undefined): string {
  const first = (name ?? '').trim().charAt(0);
  return first ? first.toUpperCase() : '•';
}

/**
 * A stable two-stop gradient for a competitor's lettermark tile, seeded off the
 * name so the same club/player always reads the same colour — the premium,
 * crest-free way to make two sides instantly distinguishable (no logos exist in
 * this data). Hues are drawn from the brand family so it stays on-identity, and
 * are kept deep/translucent so the cyan accent + scores remain the loudest thing.
 */
const TILE_GRADIENTS: readonly [string, string][] = [
  ['#1E3A8A', '#0B6FB0'], // ocean blue
  ['#5B21B6', '#8A2BE2'], // violet
  ['#0E7490', '#0FA8CE'], // teal
  ['#9D174D', '#D600FF'], // magenta
  ['#1E5128', '#2E8B4E'], // pitch green
  ['#92400E', '#C28A00'], // amber
];
function tileGradient(seed: string): [string, string] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return TILE_GRADIENTS[h % TILE_GRADIENTS.length];
}

/**
 * The competitor avatar tile: a 44px rounded-square with depth. When the side is
 * a nation (a clean country code is present) the flag fills it; otherwise a
 * seeded gradient lettermark stands in for the missing crest. A live/winning
 * side wears a bright cyan ring; a losing finished side desaturates to a flat
 * inset so the eye lands on the winner.
 */
function CompetitorTile({
  name,
  flag,
  ring,
  dim,
}: {
  name: string;
  flag: string;
  ring: boolean;
  dim: boolean;
}) {
  const palette = useThemePalette();
  const [from, to] = tileGradient(name || '•');
  return (
    <View
      className="h-11 w-11 overflow-hidden rounded-2xl"
      style={{
        borderWidth: ring ? 1.5 : 1,
        borderColor: ring ? palette.voltLine : palette.glassBorder,
        opacity: dim ? 0.5 : 1,
      }}
    >
      {flag ? (
        <View className="h-full w-full items-center justify-center bg-inset">
          <AppText
            accessibilityElementsHidden
            allowFontScaling={false}
            style={{ fontSize: 24, lineHeight: 30 }}
          >
            {flag}
          </AppText>
        </View>
      ) : (
        <LinearGradient
          colors={[from, to]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          className="h-full w-full items-center justify-center"
        >
          <AppText
            variant="heading"
            weight="bold"
            allowFontScaling={false}
            style={{ color: '#FFFFFF' }}
          >
            {monogram(name)}
          </AppText>
        </LinearGradient>
      )}
    </View>
  );
}

/**
 * One competitor row: the avatar tile, the name, and the side's score pinned BIG
 * to the trailing edge. `dim` desaturates a losing finished side so the winner
 * reads at a glance; `accent` paints the score in the live/winner cyan.
 */
function CompetitorRow({
  name,
  flag,
  score,
  accent,
  dim,
}: {
  name: string;
  flag: string;
  score: string | null;
  accent: boolean;
  dim: boolean;
}) {
  return (
    <View className="flex-row items-center gap-3">
      <CompetitorTile name={name} flag={flag} ring={accent} dim={dim} />
      <AppText
        variant="body"
        weight="bold"
        numberOfLines={1}
        className={`flex-1 text-start ${dim ? 'text-muted' : 'text-ink'}`}
      >
        {name}
      </AppText>
      {score !== null ? (
        <AppText
          weight="bold"
          tabular
          className={`min-w-[28px] text-end ${
            accent ? 'text-volt-bright' : dim ? 'text-muted' : 'text-ink'
          }`}
          style={{ fontSize: 26, lineHeight: 30 }}
        >
          {score}
        </AppText>
      ) : null}
    </View>
  );
}

/**
 * One match card: a chip header (tournament + round) over two competitor rows —
 * each a gradient/flag avatar tile, a name, and a BIG trailing score. The same
 * card renders team sports (csgo/hockey/basketball) and player sports
 * (tennis/darts); the only difference is whether a side is a club or a person.
 * Live cards wear a glowing accent rail + cyan wash + a state-of-play line;
 * finished cards desaturate the losing side and stamp an FT chip; upcoming cards
 * show a clean kickoff-time pill.
 */
function SportsCard({ event, mode }: { event: SportsEvent; mode: SportsMode }) {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const { numeralOptions } = useLocale();

  const isLive = mode === 'live';
  const isFinished = mode === 'finished';
  const hasScore = event.score_a !== null && event.score_b !== null;

  // Localized per-side score strings (or null pre-match). Scores are tabular and
  // never mirrored — each lives on its own competitor row, so RTL needs no swap.
  const scoreA = hasScore ? localizeDigits(String(event.score_a), numeralOptions.arabicNumerals) : null;
  const scoreB = hasScore ? localizeDigits(String(event.score_b), numeralOptions.arabicNumerals) : null;

  // Winner emphasis on a finished card: dim the side that lost.
  const aWon = isFinished && hasScore && Number(event.score_a) > Number(event.score_b);
  const bWon = isFinished && hasScore && Number(event.score_b) > Number(event.score_a);

  // The trailing status pill: a live badge, an FT chip, or a clean kickoff-time
  // pill. The live_label (e.g. "Map 2 · 14-12") rides its own line below the
  // header so it never gets squeezed against the LIVE chip on a narrow card.
  const statusPill = (() => {
    if (isLive) return <LiveBadge />;
    if (isFinished) {
      return (
        <View className="rounded-full border border-glass-border bg-inset px-2.5 py-1">
          <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-soft">
            {t('common.ft')}
          </AppText>
        </View>
      );
    }
    // A missing/malformed date must never reach formatKickoffTime (Invalid Date
    // makes Intl throw) — show a neutral dash instead.
    const kickoff = event.date ? new Date(event.date) : null;
    const kickoffLabel =
      kickoff && !Number.isNaN(kickoff.getTime())
        ? formatKickoffTime(kickoff, numeralOptions)
        : '—';
    return (
      <View className="flex-row items-center gap-1.5 rounded-full border border-glass-border bg-inset px-2.5 py-1">
        <Ionicons name="time-outline" size={12} color={palette.voltBright} />
        <AppText variant="micro" weight="bold" tabular className="text-soft">
          {kickoffLabel}
        </AppText>
      </View>
    );
  })();

  return (
    <Card
      className="mx-4 mb-2.5 overflow-hidden p-0"
      style={
        isLive
          ? {
              borderColor: palette.voltLine,
              // A soft cyan halo so a live card lifts off the dark canvas.
              shadowColor: palette.volt,
              shadowOpacity: 0.22,
              shadowRadius: 16,
              shadowOffset: { width: 0, height: 0 },
              elevation: 6,
            }
          : undefined
      }
    >
      {/* A whisper of cyan wash across a live card — depth without a loud block. */}
      {isLive ? (
        <LinearGradient
          colors={[palette.volt + '1F', 'rgba(0,0,0,0)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0.9 }}
          style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 }}
          pointerEvents="none"
        />
      ) : null}

      {/* The live accent rail — a vibrant vertical gradient down the leading edge. */}
      {isLive ? (
        <LinearGradient
          colors={[palette.voltBright, palette.volt, palette.voltDim]}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: 4 }}
          pointerEvents="none"
        />
      ) : null}

      <View className="px-4 py-3.5">
        {/* Header: tournament + round as clean glass chips, status on the trailing edge. */}
        <View className="mb-3 flex-row items-center justify-between gap-2">
          <View className="flex-1 flex-row items-center gap-1.5">
            <View className="max-w-full flex-row items-center gap-1.5 rounded-full border border-glass-border bg-inset px-2.5 py-1">
              <Ionicons name="trophy-outline" size={11} color={palette.voltBright} />
              <AppText
                variant="micro"
                weight="bold"
                numberOfLines={1}
                className="shrink uppercase tracking-wide text-soft"
              >
                {event.tournament}
              </AppText>
            </View>
            {/* The round (e.g. "Quarter-final", "Round of 16") as a secondary
                format chip — the only structural label this data carries. */}
            {event.round ? (
              <View className="rounded-full border border-glass-border bg-glass-hi px-2 py-1">
                <AppText
                  variant="micro"
                  weight="bold"
                  numberOfLines={1}
                  className="uppercase tracking-wide text-muted"
                >
                  {event.round}
                </AppText>
              </View>
            ) : null}
          </View>
          {statusPill}
        </View>

        <View className="gap-2.5">
          <CompetitorRow
            name={event.side_a ?? ''}
            flag={flagFromCountryCode(event.country_a)}
            score={scoreA}
            accent={isLive || aWon}
            dim={bWon}
          />
          <CompetitorRow
            name={event.side_b ?? ''}
            flag={flagFromCountryCode(event.country_b)}
            score={scoreB}
            accent={isLive || bWon}
            dim={aWon}
          />
        </View>

        {/* The live state-of-play (e.g. "Map 2 · 14-12") on its own glowing line,
            with a pulsing dot — the SofaScore-style "what's happening right now". */}
        {isLive && event.live_label ? (
          <View className="mt-3 flex-row items-center gap-2 border-t border-glass-border pt-2.5">
            <View className="h-1.5 w-1.5 rounded-full bg-live" />
            <AppText
              variant="micro"
              weight="bold"
              numberOfLines={1}
              className="flex-1 text-start text-volt-bright"
            >
              {event.live_label}
            </AppText>
          </View>
        ) : null}
      </View>
    </Card>
  );
}

/**
 * Horizontal sport selector: a compact glyph-tile + label chip per discipline.
 * The selected chip lifts on the Volt accent (glass fill, accent border, bright
 * label); the rest stay quiet inset pills. Tight, modern, never an empty void.
 */
function SportChips({
  selectedKey,
  onChange,
}: {
  selectedKey: string;
  onChange: (c: Chip) => void;
}) {
  const { t } = useTranslation();
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ paddingHorizontal: 16, gap: 8, paddingVertical: 6 }}
    >
      {SPORTS.map((chip) => {
        const selected = chipKey(chip) === selectedKey;
        return (
          <Pressable
            key={chipKey(chip)}
            accessibilityRole="tab"
            accessibilityState={{ selected }}
            accessibilityLabel={t(`sports.${chip.i18nId}`)}
            onPress={() => onChange(chip)}
            className={`flex-row items-center gap-2 rounded-full border py-1.5 pl-1.5 pr-3.5 active:opacity-80 ${
              selected ? 'border-volt-line bg-volt-glass' : 'border-glass-border bg-inset'
            }`}
          >
            <View
              className={`h-7 w-7 items-center justify-center rounded-full ${
                selected ? 'bg-volt-glass-strong' : 'bg-surface-2'
              }`}
            >
              <AppText variant="caption" allowFontScaling={false}>
                {chip.emoji}
              </AppText>
            </View>
            <AppText
              variant="micro"
              weight="bold"
              numberOfLines={1}
              className={selected ? 'text-volt-bright' : 'text-soft'}
            >
              {t(`sports.${chip.i18nId}`)}
            </AppText>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

/** Live / Upcoming / Finished toggle - a three-up segmented control. */
function ModeToggle({
  mode,
  onChange,
}: {
  mode: SportsMode;
  onChange: (m: SportsMode) => void;
}) {
  const { t } = useTranslation();
  return (
    <View className="mx-4 mb-3 mt-1 flex-row gap-1 rounded-full border border-line bg-inset p-1">
      {MODES.map((m) => {
        const selected = m === mode;
        const isLiveTab = m === 'live';
        return (
          <Pressable
            key={m}
            accessibilityRole="tab"
            accessibilityState={{ selected }}
            onPress={() => onChange(m)}
            className={`flex-1 flex-row items-center justify-center gap-1.5 rounded-full py-2 ${
              selected ? 'bg-volt-glass-strong' : ''
            }`}
          >
            {isLiveTab ? (
              <View className={`h-1.5 w-1.5 rounded-full ${selected ? 'bg-live' : 'bg-inactive'}`} />
            ) : null}
            <AppText
              variant="micro"
              weight="semibold"
              className={selected ? 'text-volt-bright' : 'text-muted'}
            >
              {t(`sports.mode.${m}`)}
            </AppText>
          </Pressable>
        );
      })}
    </View>
  );
}

/** A single shimmer-free skeleton card — the same silhouette as a real match card
 *  so the list never jumps when data lands. */
function SkeletonCard() {
  return (
    <Card className="mx-4 mb-2.5 px-4 py-3.5">
      <View className="mb-3 flex-row items-center justify-between">
        <View className="h-6 w-28 rounded-full bg-glass-hi" />
        <View className="h-6 w-14 rounded-full bg-glass-hi" />
      </View>
      <View className="gap-2.5">
        <View className="flex-row items-center gap-3">
          <View className="h-11 w-11 rounded-2xl bg-glass-hi" />
          <View className="h-4 flex-1 rounded-full bg-glass-hi" />
          <View className="h-6 w-7 rounded-md bg-glass-hi" />
        </View>
        <View className="flex-row items-center gap-3">
          <View className="h-11 w-11 rounded-2xl bg-glass-hi" />
          <View className="h-4 w-3/4 rounded-full bg-glass-hi" />
          <View className="h-6 w-7 rounded-md bg-glass-hi" />
        </View>
      </View>
    </Card>
  );
}

/** Loading slate: a short run of skeleton cards (compact, never a giant spinner). */
function SkeletonList() {
  return (
    <View className="pt-1.5">
      {[0, 1, 2, 3].map((i) => (
        <SkeletonCard key={i} />
      ))}
    </View>
  );
}

/**
 * Compact, intentional empty state — a small framed icon, a tight title + line,
 * and (for the live slice) the "see what's upcoming" CTA. Deliberately sized so
 * a quiet sport never reads as a broken, empty screen.
 */
function SportsEmpty({
  mode,
  sportLabel,
  onSeeUpcoming,
}: {
  mode: SportsMode;
  sportLabel: string;
  onSeeUpcoming?: () => void;
}) {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const icon: keyof typeof Ionicons.glyphMap =
    mode === 'live' ? 'flash-outline' : mode === 'finished' ? 'time-outline' : 'calendar-outline';
  return (
    <View className="px-4 pt-6">
      <Card className="items-center px-6 py-9">
        <View className="mb-3 h-14 w-14 items-center justify-center rounded-full border border-glass-border bg-inset">
          <Ionicons name={icon} size={24} color={palette.muted} />
        </View>
        <AppText variant="heading" className="text-center">
          {t(`sports.empty.${mode}`, { sport: sportLabel })}
        </AppText>
        <AppText variant="caption" className="mt-1.5 max-w-[260px] text-center leading-5">
          {t('sports.emptyBody')}
        </AppText>
        {mode === 'live' && onSeeUpcoming ? (
          <Pressable
            accessibilityRole="button"
            onPress={onSeeUpcoming}
            className="mt-4 flex-row items-center gap-1.5 rounded-full border border-volt-line bg-volt-glass px-4 py-2 active:opacity-80"
          >
            <AppText variant="micro" weight="bold" className="text-volt-bright">
              {t('sports.seeUpcoming')}
            </AppText>
            <Ionicons name="arrow-forward" size={13} color={palette.voltBright} />
          </Pressable>
        ) : null}
      </Card>
    </View>
  );
}

export default function SportsScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const [chip, setChip] = useState<Chip>(SPORTS[0]);
  const [mode, setMode] = useState<SportsMode>('live');
  // The day chosen on the 365 date strip (Bzzoiro sports ignore it). Resets to
  // today whenever the strip is shown afresh.
  const [selectedDate, setSelectedDate] = useState(() => new Date());

  const isBzzoiro = chip.source === 'bzzoiro';

  // 365 sports get a Yesterday/Today/Tomorrow date strip; "today" is left as an
  // absent date so the hook keeps its live-polling + tomorrow-merge path, while
  // any other day is sent as an explicit YYYY-MM-DD (a static, un-polled slate).
  const isToday = isSameDay(selectedDate, new Date());
  const dateParam = !isBzzoiro && !isToday ? dayKey(selectedDate) : undefined;

  // Switching discipline starts the date strip fresh on today, so a day left
  // selected on one 365 sport doesn't silently carry into the next one.
  const onSelectChip = (next: Chip) => {
    setChip(next);
    setSelectedDate(new Date());
  };

  // Picking a day on the 365 strip nudges the status filter to what that day can
  // actually contain — past days have no live/upcoming games, future days have no
  // finished ones — so the user never lands on an inevitably-empty slice. The
  // toggle stays free to override afterward.
  const onSelectDay = (day: Date) => {
    setSelectedDate(day);
    const now = new Date();
    if (isSameDay(day, now)) return; // today keeps whatever's selected (incl. live)
    setMode((current) => {
      if (current !== 'live') return current; // a deliberate upcoming/finished pick stands
      return day.getTime() < now.getTime() ? 'finished' : 'upcoming';
    });
  };

  // Both hooks always run (hooks can't be conditional), but only the one matching
  // the selected chip's provider is enabled — the other stays idle. A 365 chip
  // never fires a Bzzoiro request and vice-versa.
  const bzzoiroQuery = useSportsEvents(
    isBzzoiro ? (chip.sport as SportId) : 'tennis',
    mode,
    isBzzoiro,
  );
  const scores365Query = useScores365(
    isBzzoiro ? 5 : SCORES_365_SPORTS[chip.sport as Scores365Sport],
    mode,
    !isBzzoiro,
    dateParam,
  );

  const query = isBzzoiro ? bzzoiroQuery : scores365Query;
  const events = useMemo(() => query.data?.events ?? [], [query.data]);

  const renderEvent = useCallback(
    ({ item }: { item: SportsEvent }) => <SportsCard event={item} mode={mode} />,
    [mode],
  );

  return (
    <Screen>
      <BackHeader title={t('sports.title')} />
      <SportChips selectedKey={chipKey(chip)} onChange={onSelectChip} />
      {/* 365 sports navigate by day (date strip); Bzzoiro sports keep the plain
          Live/Upcoming/Finished toggle with no date concept. */}
      {!isBzzoiro ? (
        <View className="pb-1 pt-1">
          <DateStrip selected={selectedDate} onSelect={onSelectDay} />
        </View>
      ) : null}
      <ModeToggle mode={mode} onChange={setMode} />

      {query.isPending ? (
        <SkeletonList />
      ) : events.length === 0 ? (
        <SportsEmpty
          mode={mode}
          sportLabel={t(`sports.${chip.i18nId}`)}
          onSeeUpcoming={mode === 'live' ? () => setMode('upcoming') : undefined}
        />
      ) : (
        <FlatList
          data={events}
          keyExtractor={(e) => String(e.id)}
          renderItem={renderEvent}
          contentContainerStyle={{ paddingBottom: 36, paddingTop: 6 }}
          keyboardShouldPersistTaps="handled"
          removeClippedSubviews
          initialNumToRender={8}
          windowSize={7}
          ListFooterComponent={
            // A quiet footer note so a short list never looks broken.
            <Pressable
              accessibilityRole="button"
              onPress={() => router.push('/today' as never)}
              className="mt-2 items-center px-4 py-3 active:opacity-70"
            >
              <AppText variant="micro" weight="medium" className="text-muted">
                {t(isBzzoiro ? 'sports.footnote' : 'sports.footnote365')}
              </AppText>
            </Pressable>
          }
        />
      )}
    </Screen>
  );
}
